/**
 * Sharing helpers. WhatsApp (wa.me) is the Pakistani norm for receipts/reminders; the
 * system Web Share sheet is offered as a cross-platform fallback/alternative.
 * On iOS the PWA has no File System Access API, so text share (not file save) is used here.
 */

export function whatsappUrl(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`
}

/** Normalize a Pakistani/local number to wa.me international format (no '+'). */
export function toWaPhone(phone: string): string {
  let d = phone.replace(/\D/g, '')
  if (d.startsWith('00')) d = d.slice(2)
  if (d.startsWith('0')) d = '92' + d.slice(1)
  else if (d.startsWith('3') && d.length === 10) d = '92' + d
  return d
}

/** WhatsApp deep link to a SPECIFIC number with prefilled text. */
export function whatsappToPhoneUrl(phone: string, text: string): string {
  return `https://wa.me/${toWaPhone(phone)}?text=${encodeURIComponent(text)}`
}

/** Open WhatsApp with the prefilled message (no specific recipient). */
export function openWhatsApp(text: string): void {
  window.open(whatsappUrl(text), '_blank', 'noopener')
}

/** Open WhatsApp addressed to a specific customer number. */
export function openWhatsAppTo(phone: string, text: string): void {
  window.open(whatsappToPhoneUrl(phone, text), '_blank', 'noopener')
}

/** Share an image file via the native sheet (iOS/Android). Resolves false if unsupported. */
export async function shareImageFile(blob: Blob, filename: string): Promise<boolean> {
  return shareFile(blob, filename, 'image/png')
}

/** Share any file (CSV/PDF/PNG) via the native Web Share sheet; false if unsupported. */
export async function shareFile(blob: Blob, filename: string, mime: string): Promise<boolean> {
  const nav = navigator as Navigator & {
    share?: (data: { files?: File[]; text?: string }) => Promise<void>
    canShare?: (data: { files?: File[] }) => boolean
  }
  if (typeof nav.share !== 'function') return false
  try {
    const file = new File([blob], filename, { type: mime })
    if (nav.canShare && !nav.canShare({ files: [file] })) return false
    await nav.share({ files: [file] })
    return true
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return true
    return false
  }
}

/** Download a blob locally (fallback when Web Share is unavailable). */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Use the native share sheet when available. Resolves true if the sheet was shown
 * (including when the user dismissed it); false when share is unavailable/failed so
 * the caller can fall back to WhatsApp.
 */
export async function shareViaSystem(text: string): Promise<boolean> {
  const nav = navigator as Navigator & { share?: (data: { text: string }) => Promise<void> }
  if (typeof nav.share !== 'function') return false
  try {
    await nav.share({ text })
    return true
  } catch (e) {
    // AbortError = user dismissed the sheet (handled); anything else = real failure.
    if (e instanceof DOMException && e.name === 'AbortError') return true
    return false
  }
}
